import { z } from "zod";

/**
 * Structured per-criterion lead-scoring breakdown (PREC-3499) powering the
 * beautified Lead Scoring hover. Mirrors the API's
 * `score_persona_category_result.leadScoringBreakdown` JSON column.
 */
export const LeadScoringCriterionBreakdownSchema = z.object({
	label: z.string(),
	score: z.number(),
	reasoning: z.string(),
	/**
	 * Set by the scoring engine when this criterion is why the lead is
	 * disqualified. Not inferable from `score`: the API clamps it to >= 0, so a
	 * criterion that matched the rubric's Disqualified bucket and one whose value
	 * merely could not be identified both arrive as 0.
	 */
	disqualifying: z.boolean().optional(),
});

export const LeadScoringBreakdownSchema = z.object({
	company: z.array(LeadScoringCriterionBreakdownSchema),
	persona: z.array(LeadScoringCriterionBreakdownSchema),
	overall: z.object({ score: z.number(), max: z.number() }),
});

export type LeadScoringBreakdown = z.infer<typeof LeadScoringBreakdownSchema>;
export type LeadScoringCriterionBreakdown = z.infer<typeof LeadScoringCriterionBreakdownSchema>;

export const ScoreProspectCategorySchema = z.object({
	id: z.number().optional(),
	description: z.string(),
	category: z.string(),
	jobsToInclude: z.array(z.string()),
	jobsToExclude: z.array(z.string()),
	investors: z.boolean(),
});

export const PersonaCategoryResultsSchema = z.object({
	id: z.number(),
	result: z.string(),
	reason: z.string(),
	personaScore: z.string().nullable(),
	scorePersonaCategory: ScoreProspectCategorySchema.nullable(),
	personaScoringSummary: z.string().nullable(),
	overallScore: z.string().nullable(),
	overallReasoning: z.string().nullable(),
	companyScore: z.string().nullable(),
	companyScoringSummary: z.string().nullable(),
	leadScoringBreakdown: LeadScoringBreakdownSchema.nullable().optional(),
});

export type PersonaCategoryResults = z.infer<typeof PersonaCategoryResultsSchema>;
